import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, MapPin, Send } from "lucide-react";
import { PROVINCIAS_AR } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nombre: "",
  email: "",
  whatsapp: "",
  ciudad: "",
  provincia: "",
  edad: "",
  experiencia: "",
  motivacion: "",
};

export const OrganizerForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.whatsapp || !form.ciudad || !form.provincia) {
      toast.error("Completá nombre, email, WhatsApp, ciudad y provincia.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/organizers`, {
        ...form,
        edad: form.edad ? Number(form.edad) : null,
      });
      setDone(true);
      toast.success("¡Gracias por querer organizar tu ciudad! Te contactamos pronto.");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Este email ya está registrado como organizador.");
      } else {
        toast.error("No pudimos registrarte. Probá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="organizar"
      className="relative bg-white py-24 md:py-32 overflow-hidden"
      data-testid="organizer-section"
    >
      <div aria-hidden className="absolute -top-32 -left-20 w-96 h-96 bg-[#0A2463]/5 blur-3xl rounded-full" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#0A2463]" />
            <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#0A2463]">
              Referentes locales
            </span>
            <span className="h-px w-10 bg-[#0A2463]" />
          </div>
          <h2 className="font-headings font-bold uppercase tracking-tight text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Organizá Frente Ya<br />en <span className="fy-underline">tu ciudad</span>
          </h2>
          <p className="mt-6 text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed">
            El movimiento se construye en cada barrio. Si querés ser el referente
            de Frente Ya donde vivís, dejanos tus datos y te contactamos para
            coordinar las primeras actividades.
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-[#0A2463]/40 bg-[#0A2463]/5 p-10 sm:p-14 text-center"
            data-testid="organizer-success"
          >
            <CheckCircle2 className="mx-auto text-[#0A2463]" size={56} />
            <h3 className="font-headings text-3xl sm:text-4xl uppercase font-bold text-[#0A192F] mt-6">
              ¡Bienvenido/a, {form.nombre.split(" ")[0]}!
            </h3>
            <p className="mt-4 text-[#4B5563] max-w-md mx-auto">
              Vamos a contactarte por WhatsApp a <strong>{form.whatsapp}</strong> para
              empezar a organizar Frente Ya en <strong>{form.ciudad}, {form.provincia}</strong>.
            </p>
            <button
              onClick={() => {
                setForm(initial);
                setDone(false);
              }}
              className="mt-8 text-sm uppercase tracking-[0.2em] font-headings text-[#0A192F]/60 hover:text-[#0A2463] transition-colors"
              data-testid="organizer-reset"
            >
              Registrar otro referente
            </button>
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            className="border border-gray-200 bg-white p-6 sm:p-10 grid sm:grid-cols-2 gap-6 shadow-sm"
            data-testid="organizer-form"
            noValidate
          >
            <Field label="Nombre completo *">
              <input
                className="input-line"
                name="nombre"
                value={form.nombre}
                onChange={onChange}
                placeholder="Tu nombre y apellido"
                data-testid="organizer-input-nombre"
                required
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                className="input-line"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="tucorreo@ejemplo.com"
                data-testid="organizer-input-email"
                required
              />
            </Field>
            <Field label="WhatsApp *">
              <input
                type="tel"
                inputMode="tel"
                className="input-line"
                name="whatsapp"
                value={form.whatsapp}
                onChange={onChange}
                placeholder="+54 11 1234 5678"
                data-testid="organizer-input-whatsapp"
                required
              />
            </Field>
            <Field label="Ciudad / Localidad *">
              <input
                className="input-line"
                name="ciudad"
                value={form.ciudad}
                onChange={onChange}
                placeholder="Ej: Mar del Plata"
                data-testid="organizer-input-ciudad"
                required
              />
            </Field>
            <Field label="Provincia *">
              <select
                name="provincia"
                value={form.provincia}
                onChange={onChange}
                className="input-line appearance-none cursor-pointer bg-transparent"
                data-testid="organizer-input-provincia"
                required
              >
                <option value="">Elegí tu provincia</option>
                {PROVINCIAS_AR.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Edad (opcional)">
              <input
                type="number"
                min="14"
                max="120"
                className="input-line"
                name="edad"
                value={form.edad}
                onChange={onChange}
                placeholder="35"
                data-testid="organizer-input-edad"
              />
            </Field>
            <Field label="Experiencia política / social (opcional)" className="sm:col-span-2">
              <textarea
                name="experiencia"
                value={form.experiencia}
                onChange={onChange}
                rows={2}
                className="input-line resize-none"
                placeholder="¿Tenés experiencia en militancia, ONGs, sociedades de fomento, sindicatos? Contanos brevemente."
                data-testid="organizer-input-experiencia"
              />
            </Field>
            <Field label="¿Por qué querés organizar tu ciudad? (opcional)" className="sm:col-span-2">
              <textarea
                name="motivacion"
                value={form.motivacion}
                onChange={onChange}
                rows={3}
                className="input-line resize-none"
                placeholder="Tu motivación, ideas, propuestas para tu localidad."
                data-testid="organizer-input-motivacion"
              />
            </Field>

            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-[#9CA3AF] max-w-md flex items-center gap-2">
                <MapPin size={14} className="text-[#0A2463]" />
                Te contactaremos por WhatsApp para coordinar el lanzamiento en tu ciudad.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#0A2463] hover:bg-[#081b4b] disabled:opacity-60 text-white font-headings uppercase tracking-wide text-base px-8 py-4 inline-flex items-center gap-2 transition-all"
                data-testid="organizer-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Enviando…
                  </>
                ) : (
                  <>
                    Quiero ser referente <Send size={18} />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">
      {label}
    </label>
    {children}
  </div>
);

export default OrganizerForm;
